import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { VngSelectComponent, VngSelectOption } from "@vengreso/angular-components";

/**
 * Searchable FlyCut-category picker with the sticky "Create New Category"
 * action row — the `<vng-select [searchable]>` + `#vngSelectActions` block
 * that was copy-pasted across add-shortcut, duplicate-shortcut,
 * change-shortcut-category, template-copy, template-category-copy and
 * add-to-flycuts.
 *
 * The options are the host-flattened category tree (see each host's
 * `buildCategoryOptions()`, which walks `FlyCutCategory.sub_category_lv1/lv2`
 * and indents sub-level labels with NBSPs), so this component only needs
 * `VngSelectOption[]` — it never sees the raw tree.
 *
 * Creation stays host-owned: clicking the sticky row closes the panel and
 * EMITS `createCategory` with the typed search query (`""` when nothing was
 * typed); every host opens its own CategoryRenameComponent, POSTs the new
 * category, then calls {@link closePanel} on this component (via `@ViewChild`)
 * once the save resolves — exactly what they previously did against the raw
 * `VngSelectComponent`. That post-save call is now a no-op in the common path,
 * and is kept because it still matters when the panel was reopened meanwhile.
 */
@Component({
  selector: "app-category-select",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, VngSelectComponent],
  templateUrl: "./category-select.component.html",
  styleUrls: ["./category-select.component.scss"],
})
export class CategorySelectComponent {
  /**
   * Host-owned control holding the selected category id. Passed straight to
   * the inner `[formControl]` so validation/touched/dirty state keeps living
   * on the host's form (all six call sites bound `[formControl]` directly).
   */
  @Input() control: FormControl;

  /** Flattened category options (root + NBSP-indented lv1/lv2 entries). */
  @Input() options: VngSelectOption[] = [];

  /** Field label. Empty (vng-select's own default) renders no label — add-to-flycuts relies on that. */
  @Input() label = "";

  @Input() placeholder = "Pick a category";

  /** Error line under the trigger. Hosts keep computing this from their own control state. */
  @Input() error = "";

  /**
   * Hides the sticky create row entirely (the whole row, not just one label).
   * add-shortcut and template-copy pass `!isOnboarding` — creation is
   * suppressed during the onboarding tour.
   */
  @Input() canCreate = true;

  /**
   * Text of the create row when the search box is empty. Only add-shortcut
   * deviates from the default (it says "Create New Categories"). The
   * non-empty-query row is always `"<query>" (Create New Category)`.
   */
  @Input() createLabel = "Create New Category";

  /**
   * Emits the typed search query — `""` when the empty-search row was clicked.
   * Hosts translate that into their CategoryRenameComponent payload
   * (`$event ? { name: $event } : {}`).
   */
  @Output() createCategory = new EventEmitter<string>();

  /** Pass-through of vng-select's `selectionChange` (change-shortcut-category reads `option.data.level` from it). */
  @Output() selectionChange = new EventEmitter<any>();

  @ViewChild(VngSelectComponent) select: VngSelectComponent;

  /** Closes the dropdown panel — hosts call this after their create-category POST resolves. */
  closePanel(): void {
    this.select?.closePanel();
  }

  /**
   * Sticky create-row click. The panel is dismissed here rather than left to
   * the host's post-save `closePanel()`: the host opens CategoryRenameComponent
   * synchronously, and vng-select body-portals its panel, so the open dropdown
   * otherwise stayed stacked on top of the "Add Category" dialog until the POST
   * resolved (or forever, if the dialog was cancelled).
   */
  onCreate(query: string): void {
    this.closePanel();
    this.createCategory.emit(query);
  }

  /**
   * Hides the create row when the typed query already matches an existing
   * category name exactly (NBSP indentation stripped) — same semantics as the
   * identical helper each host used to declare.
   */
  hasExactCategoryMatch(query: string): boolean {
    if (!query) return false;
    const q = query.trim().toLowerCase();
    return this.options.some((o) => o.label.replace(/\u00A0/g, "").trim().toLowerCase() === q);
  }
}
